import lodash from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogKeyboardDatePickerContainer from '@/main/components/shared/datepicker/keyboardDatePickerContainer';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import {
  getDefaultCountryOptionDrop,
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  removeEmptyValues
} from '@/main/services/utils';
import { AreaButton } from '@/main/components/shared/styles';

import { procurementReportApi } from '@/main/api';
import { KeyValue, KeyValueDefault } from '@/main/models/common';
import {
  BodyShopFilterRequest,
  ExportSupplierPerformanceRequest,
  InsurerFilterRequest,
  SearchGridSupplierPerformanceRequest,
  StatesFilterRequest,
  SupplierFilterRequest
} from '@/main/models/request';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import {
  formatDateToISOString,
  initalDateForm,
  onLoadDateRangePicker,
  validateFormDate
} from '@/main/services/utilsDate';
import { downloadFile } from '@/main/services/utilsFile';
import {
  userIsBodyshop,
  userIsInsurer,
  userIsSupplier
} from '@/main/services/utilsUser';
import {
  getBodyshops,
  resetReducerBodyshops,
  useBodyshopReducer
} from '@/main/store/bodyshop';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import {
  getSuppliers,
  resetReducerSuppliers,
  useSupplierReducer
} from '@/main/store/supplier';

import * as S from './styles';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import KogInputAutoCompleteCheckControlled from '@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled';
import {
  getStatesByBodyshop,
  resetReducerStatesByBodyshop,
  useStateReducer
} from '@/main/store/state';

interface IFiltersSupplierPerformanceProps {
  showFilters: boolean;
  formFilters: SearchGridSupplierPerformanceRequest | null;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  onResetSearch: () => void;
  getDataGrid: (filters: SearchGridSupplierPerformanceRequest) => void;
}

function FiltersSupplierPerformance({
  formFilters,
  showFilters,
  getDataGrid,
  onResetSearch,
  setShowFilters
}: IFiltersSupplierPerformanceProps) {
  const parameterRangeDays = useParameterByTypeScreenReducer(
    ParameterTypeEnum.DefaultSupplierPerformanceDaysInterval
  );
  // #region Form constants
  const INITIAL_FORM_STATE = {
    country: null as KeyValue | null,
    insurer: null as KeyValue | null,
    bodyshop: null as KeyValue | null,
    supplier: null as KeyValue | null,
    startDate: initalDateForm(parameterRangeDays),
    finalDate: initalDateForm(),
    bodyshopState: [] as KeyValue[]
  };
  type IFormFiltersSupplierPerformance = typeof INITIAL_FORM_STATE;
  //#endregion

  // #region Reducers/Options
  const { countriesUser: countryReducer } = useCountryReducer();
  const { insurers: insurerReducer } = useInsurerReducer();
  const { bodyshops: bodyshopReducer } = useBodyshopReducer();
  const { suppliers: supplierReducer } = useSupplierReducer();
  const { statesUserDomainByBodyshop: statesBodyshopReducer } =
    useStateReducer();
  // #endregion

  // #region Hooks
  const dispatch = useDispatch();
  const methods = useFormQueryParams<IFormFiltersSupplierPerformance>({
    defaultValues: INITIAL_FORM_STATE,
    onSubmit: onSubmitFilter,
    mapDefaultValues: {
      country: countryReducer,
      bodyshop: bodyshopReducer,
      insurer: insurerReducer,
      supplier: supplierReducer
    }
  });
  const { control, reset, watch, getValues, setValue, customHandleSubmit } =
    methods;
  //#endregion

  // #region States/Watch
  const optionDefaultCountryDrop = getDefaultOptionDrop(countryReducer.data);
  const optionDefaultInsurerDrop = userIsInsurer()
    ? getDefaultOptionDrop(insurerReducer.data)
    : null;
  const optionDefaultSupplierDrop = getDefaultOptionDrop(supplierReducer.data);

  const selectedStates = watch('bodyshopState') || [];

  // #endregion

  // #region Async Functions
  async function onSubmitFilter(data: IFormFiltersSupplierPerformance) {
    validateFormDate(data.startDate, data.finalDate, parameterRangeDays);

    if (isNullUndefined(data.country)) return;
    const request: SearchGridSupplierPerformanceRequest = {
      guidCountry: String(data.country.key),
      guidInsurer: data.insurer?.key ? String(data.insurer.key) : null,
      guidBodyShop: data.bodyshop?.key ? String(data.bodyshop.key) : null,
      guidSupplier: data.supplier?.key ? String(data.supplier.key) : null,
      initialDate: formatDateToISOString(data.startDate),
      finalDate: formatDateToISOString(data.finalDate),
      guidsStatesBodyShop: data.bodyshopState?.map(s => String(s.key)) || null,
    };
    getDataGrid(removeEmptyValues(request));
    setShowFilters(false);
  }
  // #endregion

  // #region Handle Functions
  function handleClear() {
    reset(INITIAL_FORM_STATE);
    setValue('country', optionDefaultCountryDrop);
    if (userIsSupplier()) setValue('supplier', optionDefaultSupplierDrop);
    const { firstDateResult, secondDateResult } =
      onLoadDateRangePicker(parameterRangeDays);
    setValue('startDate', firstDateResult);
    setValue('finalDate', secondDateResult);
    callInsurerApiDomain();
    callBodyshopApiDomain();
    onResetSearch();
  }
  // #endregion

  // #region Call reducers function
  function callInsurerApiDomain(companySelected: KeyValue | null = null) {
    // Clean fields & reducers
    dispatch(resetReducerInsurers());

    if (optionDefaultCountryDrop !== null) {
      const request: InsurerFilterRequest = {
        ...(companySelected && { guidBodyShop: String(companySelected.key) }),
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };

      dispatch(getInsurers(request));
    }
  }

  function callBodyshopApiDomain(insurerSelected: KeyValue | null = null) {
    // Clean fields & reducers
    dispatch(resetReducerBodyshops());

    if (optionDefaultCountryDrop !== null) {
      const request: BodyShopFilterRequest = {
        ...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getBodyshops(request));
    }
  }

  function callSupplierApiDomain() {
    // Clean fields & reducers
    dispatch(resetReducerSuppliers());

    if (optionDefaultCountryDrop !== null) {
      const request: SupplierFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getSuppliers(request));
    }
  }

  function callStateApiDomain(bodyshopSelected: KeyValue | null = null) {
    dispatch(resetReducerStatesByBodyshop());

    if (optionDefaultCountryDrop !== null) {
      const request: StatesFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        ...(bodyshopSelected && { guidBodyshop: String(bodyshopSelected.key) })
      };
      dispatch(getStatesByBodyshop(request));
    }
  }

  //#endregion

  // #region UseEffects
  useEffect(() => {
    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());

    return () => {
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerInsurers());
      dispatch(resetReducerSuppliers());
      dispatch(resetReducerStatesByBodyshop());
    };
  }, []);

  useEffect(() => {
    callSupplierApiDomain();
    callInsurerApiDomain();
    callBodyshopApiDomain();
    callStateApiDomain();
  }, [optionDefaultCountryDrop?.key]);

  useEffect(() => {
    if (optionDefaultInsurerDrop?.key != null) {
      callBodyshopApiDomain(optionDefaultInsurerDrop);
    }
  }, [optionDefaultInsurerDrop?.key]);
  // #endregion
  const hasBodyshopDefault =
    userIsBodyshop() && bodyshopReducer?.data?.length === 1;

  const disabledCountry =
    isNullUndefined(countryReducer.data) || countryReducer?.data?.length === 1;
  const disabledInsurer = userIsInsurer();
  const disabledBodyshop =
    isNullUndefined(bodyshopReducer.data) || hasBodyshopDefault;
  const disabledSupplier =
    isNullUndefined(supplierReducer.data) || userIsSupplier();
  return (
    <Box showBox={showFilters} title={getTranslateByKey('information.filters')}>
      <form onSubmit={customHandleSubmit} noValidate>
        <S.Container>
          <KogAutoCompleteControlled
            id="id-mappingCodes-select-country"
            nameCustom="country"
            label={getTranslateByKey('information.country')}
            isDefaultValueUnique
            valueDefaultKey={optionDefaultCountryDrop?.key}
            control={control}
            rulesCustom={{ required: true }}
            setValueCustom={setValue}
            options={countryReducer.data}
            isLoading={countryReducer.isLoading}
            disabled={disabledCountry}
            onChangeCustom={() => {}}
          />
          <KogAutoCompleteControlled
            nameCustom="insurer"
            id="id-searchOrder-select-insurer"
            isDefaultValueUnique={optionDefaultInsurerDrop !== null}
            setValueCustom={setValue}
            label={getTranslateByKey('information.insurer')}
            control={control}
            rulesCustom={{ required: false }}
            errorMessageCustom=""
            hasErrorCustom={false}
            options={insurerReducer.data}
            isLoading={insurerReducer.isLoading}
            disabled={disabledInsurer}
            onChangeCustom={(optionSelected: KeyValue | null) => {
              if (optionSelected === null) {
                const bodyshop = getValues().bodyshop;
                bodyshop != null
                  ? callInsurerApiDomain(bodyshop)
                  : callInsurerApiDomain();
              }
              callBodyshopApiDomain(optionSelected);
            }}
          />

          <KogAutoCompleteControlled
            nameCustom="bodyshop"
            id="id-searchOrder-select-bodyshop"
            isDefaultValueUnique={disabledBodyshop}
            setValueCustom={setValue}
            label={getTranslateByKey('information.bodyshopBranch')}
            control={control}
            rulesCustom={{ required: false }}
            options={bodyshopReducer.data}
            isLoading={bodyshopReducer.isLoading}
            disabled={disabledBodyshop}
            onChangeCustom={(optionSelected: KeyValue | null) => {
              const isTypeCompanyUserInsurer = userIsInsurer();
              if (optionSelected === null) {
                optionDefaultInsurerDrop != null
                  ? callBodyshopApiDomain(optionDefaultInsurerDrop)
                  : callBodyshopApiDomain();
              }
              if (!isTypeCompanyUserInsurer)
                callInsurerApiDomain(optionSelected);

              setValue('bodyshopState', []);
              callStateApiDomain(optionSelected);
            }}
          />
          <KogAutoCompleteControlled
            nameCustom="supplier"
            control={control}
            isDefaultValueUnique
            setValueCustom={setValue}
            id="id-searchOrder-select-supplier"
            label={getTranslateByKey('information.supplier')}
            rulesCustom={{ required: false }}
            options={supplierReducer.data}
            isLoading={supplierReducer.isLoading}
            disabled={disabledSupplier}
            onChangeCustom={() => {}}
          />
          <KogKeyboardDatePickerContainer
            idScreen="id-searchOrder"
            nameStartDate="startDate"
            nameFinalDate="finalDate"
            parameterRangeDays={parameterRangeDays}
            control={control}
            alignItemDateContainer="start"
          />
          <KogInputAutoCompleteCheckControlled
            name="bodyshopState"
            control={control}
            label={getTranslateByKey('information.bodyshopState')}
            options={statesBodyshopReducer.data || []}
            customInputProps={{
              required: false
            }}
            id="id-searchOrder-select-bodyshopState"
            disableSelectAll={false}
            selectAllLabel={getTranslateByKey('information.selectAll')}
            isLoading={statesBodyshopReducer.isLoading}
          />
          <S.AreaButtons>
            <SecondaryButton id="id-mappingCodes-btn-submit" type="submit">
              {getTranslateByKey('information.search')}
            </SecondaryButton>
            <SecondaryButton
              id="id-mappingCodes-btn-clear"
              type="button"
              onClick={handleClear}>
              {getTranslateByKey('information.clear')}
            </SecondaryButton>
          </S.AreaButtons>
        </S.Container>
      </form>
    </Box>
  );
}

function WrapperFiltersSupplierPerformance(
  props: Omit<
    IFiltersSupplierPerformanceProps,
    'showFilters' | 'setShowFilters'
  >
) {
  //#region States
  const parameterRangeDays = useParameterByTypeScreenReducer(
    ParameterTypeEnum.DefaultSupplierPerformanceDaysInterval
  );
  const { countriesUser: countryReducer } = useCountryReducer();
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [isLoadingExport, setIsLoadingExport] = useState<boolean>(false);
  const { suppliers: supplierReducer } = useSupplierReducer();
  //#endregion

  function handleFilters() {
    setShowFilters(prev => !prev);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  function exportExcel() {
    setIsLoadingExport(true);
    const cloneFormFilters = lodash.cloneDeep(props.formFilters);

    const request: ExportSupplierPerformanceRequest = {
      ...cloneFormFilters,
      initialDate: isNullUndefined(cloneFormFilters?.initialDate)
        ? formatDateToISOString(initalDateForm(parameterRangeDays))
        : cloneFormFilters.initialDate,
      finalDate: isNullUndefined(cloneFormFilters?.finalDate)
        ? formatDateToISOString(initalDateForm())
        : cloneFormFilters.finalDate,
      guidCountry: isNullUndefined(cloneFormFilters?.guidCountry)
        ? String(getDefaultCountryOptionDrop(countryReducer.data)?.key)
        : cloneFormFilters.guidCountry,
      guidSupplier: userIsSupplier()
        ? String(
            getDefaultCountryOptionDrop(
              supplierReducer.data as KeyValueDefault[]
            )?.key
          )
        : cloneFormFilters?.guidSupplier,
      pageIndex: isNullUndefined(cloneFormFilters?.pageIndex)
        ? PaginationEnum.PageIndexIntial
        : cloneFormFilters.pageIndex,
      pageSize: isNullUndefined(cloneFormFilters?.pageSize)
        ? PaginationEnum.PageSize30
        : cloneFormFilters.pageSize
    };
    procurementReportApi
      .exportSupplierPerformance(removeEmptyValues(request))
      .then(response => {
        if (response.data.result) {
          const base64File = response.data.result;
          const nameFile = getTranslateByKey('screen.suppPerformanceReport');
          downloadFile(base64File, `${nameFile}.xlsx`);
        }
      })
      .finally(() => {
        setIsLoadingExport(false);
      });
  }
  const mergedProps: IFiltersSupplierPerformanceProps = {
    ...props,
    showFilters,
    setShowFilters
  };
  return (
    <>
      <AreaButton>
        <Button
          id="id-holidays-btn-exportExcel"
          isLoading={isLoadingExport || countryReducer.isLoading}
          onClick={exportExcel}>
          {getTranslateByKey('information.exportExcel')}
        </Button>
        <Button onClick={handleFilters} id="id-holidays-btn-filters">
          {getTranslateByKey('information.filters')}
        </Button>
      </AreaButton>
      <FiltersSupplierPerformance {...mergedProps} />
    </>
  );
}

export default WrapperFiltersSupplierPerformance;
